import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "./api";
import { useAuth } from "./auth";
import { Button, rememberListLocation, useDebounced } from "./ui";

interface NavItem { to: string; label: string; icon: string; owner?: boolean; badgeKey?: string }
const NAV: { title: string; items: NavItem[] }[] = [
  { title: "Workshop", items: [
    { to: "/", label: "Dashboard", icon: "▦" },
    { to: "/enquiries", label: "Enquiries", icon: "✉", badgeKey: "enquiries" },
    { to: "/bookings", label: "Bookings", icon: "◷" },
    { to: "/jobs", label: "Jobs", icon: "🔧" },
    { to: "/quotes", label: "Quotes", icon: "✎" },
    { to: "/followups", label: "Follow-ups", icon: "☎" },
  ] },
  { title: "Records", items: [
    { to: "/customers", label: "Customers", icon: "☺" },
    { to: "/vehicles", label: "Vehicles", icon: "🚗" },
  ] },
  { title: "Money", items: [
    { to: "/invoices", label: "Invoices", icon: "£" },
    { to: "/payments", label: "Payments", icon: "⇄", owner: true },
    { to: "/expenses", label: "Expenses", icon: "🧾", owner: true },
    { to: "/suppliers", label: "Suppliers & bills", icon: "📦", owner: true },
    { to: "/stock", label: "Parts stock", icon: "⚙", owner: true },
    { to: "/staff", label: "Staff & wages", icon: "👥", owner: true },
    { to: "/bank", label: "Bank", icon: "🏦", owner: true },
    { to: "/finance", label: "Money & accounts", icon: "⚖", owner: true },
    { to: "/reports", label: "Reports", icon: "📈", owner: true },
  ] },
  { title: "Admin", items: [
    { to: "/settings", label: "Settings", icon: "⚙" },
  ] },
];

export function Layout() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => rememberListLocation(loc.pathname, loc.search), [loc.pathname, loc.search]);
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: () => api.get("/dashboard"), staleTime: 60_000 });
  const settings = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings"), staleTime: 300_000 });
  const env = settings.data?.integrations?.environment as string | undefined;
  const counts: Record<string, number> = { enquiries: dash.data?.tiles?.find((t: any) => t.key === "enquiries")?.value ?? 0 };
  const isOwner = user?.role === "owner";
  const newVersion = useNewVersionAvailable();

  return (
    <div className="shell">
      <nav className={`sidebar ${open ? "open" : ""}`} aria-label="Main">
        <div className="brand"><div className="mark">W</div><div>Wali Auto Fix<small>Garage Manager</small></div></div>
        {NAV.map((g) => {
          const items = g.items.filter((i) => !i.owner || isOwner);
          if (!items.length) return null;
          return (
            <div className="nav-group" key={g.title}>
              <div className="nav-group-title">{g.title}</div>
              {items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.to === "/"} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                  <span aria-hidden style={{ width: 20, textAlign: "center" }}>{i.icon}</span>{i.label}
                  {i.badgeKey && counts[i.badgeKey] > 0 && <span className="count">{counts[i.badgeKey]}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
        <div className="foot">
          <div style={{ color: "#fff", fontWeight: 600 }}>{user?.name}</div>
          <div>{isOwner ? "Owner" : "Staff"}</div>
          <Button size="sm" variant="ghost" style={{ color: "#dbe3f3", paddingLeft: 0 }} onClick={signOut}>Sign out</Button>
        </div>
      </nav>
      {open && <div className="scrim" onClick={() => setOpen(false)} />}
      <div className="main">
        {env && env !== "production" && <div className="env-banner">{env.toUpperCase()} environment — {env === "staging" ? "test data only, not live records" : "local development data"}</div>}
        <header className="topbar">
          <Button variant="ghost" className="menu-btn icon-btn" aria-label="Open menu" onClick={() => setOpen(true)}>☰</Button>
          <GlobalSearch />
        </header>
        {newVersion && <div className="update-bar" role="status">
          <span>A new version of Garage Manager is available.</span>
          <Button size="sm" variant="primary" onClick={() => window.location.reload()}>Reload</Button>
        </div>}
        <main className="content" id="main"><Outlet /></main>
      </div>
      <nav className="mobile-nav" aria-label="Quick">
        {[{ to: "/", l: "Home", i: "▦" }, { to: "/jobs", l: "Jobs", i: "🔧" }, { to: "/bookings", l: "Bookings", i: "◷" }, { to: "/customers", l: "Customers", i: "☺" }, { to: "/invoices", l: "Invoices", i: "£" }].map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({ isActive }) => (isActive ? "active" : "")}><span aria-hidden style={{ fontSize: 18 }}>{n.i}</span>{n.l}</NavLink>
        ))}
      </nav>
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const dq = useDebounced(q.trim(), 200);
  const navigate = useNavigate();
  const box = useRef<HTMLDivElement>(null);
  const res = useQuery({ queryKey: ["search", dq], queryFn: () => api.get(`/search${qs({ q: dq })}`), enabled: dq.length >= 2 });
  const results: { type: string; label: string; detail: string | null; href: string }[] = res.data?.results ?? [];
  useEffect(() => {
    const h = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setFocused(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const go = (href: string) => { setQ(""); setFocused(false); navigate(href); };
  let lastType = "";
  return (
    <div className="search" ref={box}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
      <input aria-label="Search customers, registrations, jobs, invoices" placeholder="Search name, phone, reg, job or invoice no." value={q}
        onChange={(e) => setQ(e.target.value)} onFocus={() => setFocused(true)}
        onKeyDown={(e) => { if (e.key === "Enter" && results[0]) go(results[0].href); if (e.key === "Escape") setFocused(false); }} />
      {focused && dq.length >= 2 && (
        <div className="search-results" role="listbox">
          {res.isLoading && <div className="loading">Searching…</div>}
          {res.isError && <div className="alert error" style={{ margin: 8 }}>Search failed. Try again.</div>}
          {res.data && !results.length && <div className="empty" style={{ padding: 16 }}>No matches for “{dq}”.</div>}
          {results.map((r, i) => {
            const head = r.type !== lastType ? <div className="group">{r.type}s</div> : null;
            lastType = r.type;
            return (
              <div key={i}>{head}
                <a href={r.href} onClick={(e) => { e.preventDefault(); go(r.href); }}><strong>{r.label}</strong>{r.detail && <span className="muted small"> · {r.detail}</span>}</a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** True when the server has a newer version of the app than the one running in this tab (a tab left open across an
 *  update keeps running the old code until it is reloaded). Checks every 5 minutes and when the tab comes back. */
function useNewVersionAvailable() {
  const [newer, setNewer] = useState(false);
  useEffect(() => {
    const running = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')?.getAttribute("src");
    if (!running) return; // development build: nothing to compare
    const check = async () => {
      try {
        const html = await (await fetch("/", { cache: "no-store", credentials: "same-origin" })).text();
        const live = /\/assets\/index-[\w-]+\.js/.exec(html)?.[0];
        if (live && !running.endsWith(live)) setNewer(true);
      } catch { /* offline: try again later */ }
    };
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    const t = window.setInterval(check, 5 * 60_000);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => { window.clearInterval(t); document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("focus", check); };
  }, []);
  return newer;
}
